/* eslint-disable consistent-return */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import { routes } from '@vlabs/shared/navigation/routes';
import { playAttentionSound } from '@vlabs/shared/utils/playAttentionSound';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  selectNotifyEvents,
  selectViewedNotifyIds,
  selectShownNotifyEventIds,
} from '@vlabs/pages/events/latest-events/selectors';
import { setShownNotifyIds, setViewedNotifyIds } from '@vlabs/pages/events/latest-events/store';

import { getArrFromLocalStorage, setArrToLocalStorage } from '@vlabs/monokit/shared/lib/localStorage';
import { NotificationToast } from '@vlabs/monokit/shared/ui/notification-toast';

import { enrichDataWithListOptions } from '../enrichDataWithListOptions';
import { filterEventsByNotifications } from '../filterEventsByNotifications';
import type { TPausedNotification } from '../types';
import st from './NotificationProvider.module.sass';

interface NotificationProviderProps {
  notifyEvents: Record<string, unknown>[];
  viewedNotifyIds: (string)[];
  shownNotifyEventIds: (string)[];
  setShownNotify: (id: string) => void;
  setViewedNotify: (id: string) => void;
  children: React.ReactElement;
}

function NotificationProviderComponent({
  notifyEvents,
  viewedNotifyIds,
  shownNotifyEventIds,
  setShownNotify,
  setViewedNotify,
  children,
}: NotificationProviderProps): React.ReactElement {
  const { pathname } = useLocation();
  const [latestId, setLatestId] = useState<string | undefined>();
  const [notifications, setNotifications] = useState<Record<string, unknown>[]>();
  const [listOptions, setListOptions] = useState<Record<string, unknown>[]>();
  const [enrichedData, setEnrichedData] = useState<Record<string, unknown>[]>();

  const [pausedNotificationArr, setPausedNotificationArr] = useState<TPausedNotification[]>([]);

  const checkPausedNotification = () => {
    const filtered = pausedNotificationArr.filter((notification) => notification.time > Date.now());
    if (filtered.length !== pausedNotificationArr.length) setPausedNotificationArr(filtered);
  };

  useEffect(() => {
    setPausedNotificationArr(getArrFromLocalStorage('pausedNotifications'));
  }, [getArrFromLocalStorage('pausedNotifications').length]);

  useEffect(() => {
    setArrToLocalStorage('pausedNotifications', pausedNotificationArr);
  }, [pausedNotificationArr]);

  useEffect(() => {
    if (!pausedNotificationArr.length) return;
    const interval = setInterval(checkPausedNotification, 60000);
    return () => clearInterval(interval);
  }, [pausedNotificationArr.length]);

  useEffect(() => {
    Promise.all([
      apiContainer.notifierClient.filters.getAll(),
      apiContainer.lunaClient.lists.getAll(),
    ]).then(([{ data: filtersData }, { data: lists }]) => {
      const filteredEventData = filtersData.filter((notification) => notification.ui.enable === true);
      setNotifications(filteredEventData);
      setListOptions(lists);
    });
  }, []);

  const disabledUntil = Number(localStorage.getItem('offUINotification')) || 0;

  const onCardClick = (event) => {
    const id = event?.event_id;
    setViewedNotify(id);
    window.open(routes.events.latest, '_blank', 'noopener,noreferrer');
  };

  const unseenEvents = useMemo(() => {
    if (!notifyEvents.length) return [];
    return notifyEvents.filter((event) => {
      const id = event.event_id;
      return !viewedNotifyIds?.includes(id) && !shownNotifyEventIds?.includes(id);
    });
  }, [notifyEvents, viewedNotifyIds, shownNotifyEventIds]);

  useEffect(() => {
    let isCancelled = false;

    const fetchEnriched = async () => {
      if (!unseenEvents?.length || !listOptions?.length) setEnrichedData([]);
      const result = await enrichDataWithListOptions(unseenEvents, listOptions);
      if (!isCancelled) setEnrichedData(result);
    };
    fetchEnriched();

    return () => {
      isCancelled = true;
    };
  }, [unseenEvents, listOptions]);

  const matchedEvents = useMemo(() => {
    if (!notifications?.length || !enrichedData?.length) return [];
    return enrichedData.filter((e) => {
      if (!notifications.length) return true;
      return notifications.some((n) => filterEventsByNotifications(e, n));
    });
  }, [notifications, enrichedData]);

  const showNotification = () => {
    if (
      pathname === routes.events.latest
    || !matchedEvents?.length
    || Date.now() < disabledUntil
    ) return;

    const lastEvent = matchedEvents.at(-1);
    const lastId = lastEvent?.event_id ?? lastEvent?.event?.event_id;

    matchedEvents.forEach((event) => {
      const id = event.event_id;

      toast.error(
        <NotificationToast
          event={event}
          onClick={() => onCardClick(event)}
        />,
        { position: 'top-right', className: st.NotificationToast },
      );
      setShownNotify(id);
    });

    if (lastId && lastId !== latestId) {
      playAttentionSound();
      setLatestId(lastId);
    }
  };

  useEffect(() => {
    showNotification();
  }, [
    matchedEvents,
    pathname,
  ]);

  return children;
}

export const NotificationProvider = connect(
  (state) => ({
    notifyEvents: selectNotifyEvents(state),
    viewedNotifyIds: selectViewedNotifyIds(state),
    shownNotifyEventIds: selectShownNotifyEventIds(state),
  }),
  (dispatch) => ({
    setShownNotify: (id) => dispatch(setShownNotifyIds({ id })),
    setViewedNotify: (id) => dispatch(setViewedNotifyIds({ id })),
  }),
)(NotificationProviderComponent);
